import { ColorBlock } from "@/components/colorblock/ColorBlock.tsx";
import { limitedColors } from "@/components/emoji/limited-colors.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import { useLabelSave } from "@/queries/use-labels.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiLabel, ApiLabelSavePayload } from "@/types/recruit/label.types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Input,
  Modal,
  ModalProps,
  Stack,
  Text,
  Title,
  toColorName,
  useModals,
} from "@skailer-com/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface LabelEditModalProps extends ModalProps {
  label: ApiLabel;
}

// const limitedColors = ["#1B1D21", "#32309C", "#7124A1", "#AB2C78", "#2F9AB2", "#AFB21D", "#B22F1D"];

export function LabelEditModal({ label, ...props }: LabelEditModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const LabelSchemaObject = z.object({
    title: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
    color: z
      .string({ error: i18n.t("errors:field_required") })
      .min(1, i18n.t("errors:field_required")),
  });

  type LabelSchema = z.infer<typeof LabelSchemaObject>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<LabelSchema>({
    mode: "onSubmit",
    resolver: zodResolver(LabelSchemaObject),
    defaultValues: {
      title: label.title,
      color: toColorName(label.color) ?? label.color ?? "brand-dark",
    },
  });

  const labelSave = useLabelSave(control);

  const onSubmit = handleSubmit(async (labelData) => {
    const payload: ApiLabelSavePayload = {
      title: labelData.title,
      color: labelData.color,
    };

    try {
      const savedLabel = await labelSave.mutateAsync({ id: label.id, payload });
      if (label.id == 0) {
        analyticsService.trackEvent(analyticEvents.labels.created, {
          [analyticProperties.id]: savedLabel.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.labels.edited, {
          [analyticProperties.id]: label.id,
        });
      }

      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={t("ats:tag")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} isLoading={labelSave.isPending} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack>
          <Input
            label={t("common:title")}
            required={true}
            autoFocus={true}
            type="text"
            {...register("title")}
            error={errors.title?.message}
          />

          <Title header={t("common:choose_color")} size={"xs"} paddingTop paddingBottom />
          <Controller
            render={({ field: { value, onChange } }) => (
              <Stack direction={"horizontal"} gap={"sm"}>
                {limitedColors.map((colorName) => (
                  <ColorBlock
                    key={colorName}
                    color={colorName}
                    size="md"
                    selected={toColorName(value) === colorName}
                    onClick={() => onChange(colorName)}
                  />
                ))}
              </Stack>
            )}
            control={control}
            name={"color"}
          />
          {errors.color?.message && (
            <Box className={"pt-2 pb-4"}>
              <Text className={"text-danger"}>{errors.color?.message}</Text>
            </Box>
          )}
        </Stack>
      </form>
    </Modal>
  );
}
